/**
 * Livello di anteprima e "conseguenze" sopra {@link MotornetEngine}.
 *
 * Prima di applicare un'azione dell'utente (selezione/deselezione) si costruisce
 * un engine di anteprima e si confronta il suo stato con quello confermato, così
 * da mostrare in anticipo cosa cambierebbe: accessori aggiunti/rimossi in
 * automatico, azione rifiutata, variazione di prezzo e nuove esclusioni.
 */
import { MotornetEngine, type FormulaOrGroup, type OrChoice } from './engine';
import type { MotornetAccessoriesResponse } from 'src/motornet/types';
import type { OptionId } from './domain';

/** Azione dell'utente su un accessorio: selezionarlo o deselezionarlo. */
export type Action = { kind: 'select' | 'deselect'; id: OptionId };

/** Alias UI per una domanda OR di una formula. */
export type OrGroup = FormulaOrGroup;

/** Stato "risolto" di una configurazione, con l'indice di chi impone ogni accessorio. */
export interface SolvedState {
	picks: OptionId[];
	configuration: OptionId[];
	total: { retail: number; base: number };
	requiredBy: Map<OptionId, OptionId | null>;
}

/**
 * Costruisce un engine di anteprima: parte dai pick confermati, applica le
 * scelte OR e infine l'azione proposta. L'engine risultante rappresenta lo stato
 * che si otterrebbe confermando l'azione, senza toccare quello reale.
 */
export async function buildPreview(
	response: MotornetAccessoriesResponse,
	vehiclePrice: number,
	committedPicks: OptionId[],
	action: Action,
	choices: OrChoice[]
): Promise<MotornetEngine> {
	const engine = await MotornetEngine.create(response, vehiclePrice);
	await engine.setChoices(choices);
	for (const id of committedPicks) await engine.select(id);
	if (action.kind === 'select') await engine.select(action.id);
	else await engine.deselect(action.id);
	return engine;
}

/** Estrae da un engine lo {@link SolvedState}, includendo la mappa `requiredBy`. */
export async function solveState(engine: MotornetEngine): Promise<SolvedState> {
	const state = await engine.state();
	const { lines } = engine.priceBreakdown();
	return {
		picks: state.picks,
		configuration: state.configuration,
		total: state.price,
		requiredBy: new Map(lines.map((l) => [l.id, l.requiredBy])),
	};
}

/**
 * Conseguenze di un'azione, calcolate confrontando stato confermato e anteprima:
 * - `rejected`: la selezione non è stata accettata (il pick non compare);
 * - `noop`: nessun cambiamento effettivo;
 * - `autoAdded` / `autoRemoved`: accessori aggiunti/rimossi in automatico dalle catene;
 * - `priceBefore` / `priceAfter`: totali prima e dopo.
 */
export interface Consequences {
	action: Action;
	rejected: boolean;
	noop: boolean;
	autoAdded: { id: OptionId; requiredBy: OptionId | null }[];
	autoRemoved: { id: OptionId; conflictsWith: OptionId }[];
	priceBefore: { retail: number; base: number };
	priceAfter: { retail: number; base: number };
}

/** Uguaglianza fra due insiemi di id. */
const sameSet = (a: Set<number>, b: Set<number>) =>
	a.size === b.size && [...a].every((x) => b.has(x));

/**
 * Confronta lo stato confermato con quello di anteprima e ne deriva le
 * {@link Consequences} da mostrare all'utente prima della conferma.
 */
export function diffConsequences(
	committed: SolvedState,
	preview: SolvedState,
	action: Action
): Consequences {
	const beforeCfg = new Set(committed.configuration);
	const afterCfg = new Set(preview.configuration);
	const afterPicks = new Set(preview.picks);

	const rejected = action.kind === 'select' && !afterPicks.has(action.id);

	const autoAdded = preview.configuration
		.filter((id) => !beforeCfg.has(id) && id !== action.id)
		.map((id) => ({ id, requiredBy: preview.requiredBy.get(id) ?? null }));

	const autoRemoved = committed.configuration
		.filter((id) => !afterCfg.has(id) && id !== action.id)
		.map((id) => ({ id, conflictsWith: action.id }));

	const noop =
		sameSet(beforeCfg, afterCfg) &&
		sameSet(new Set(committed.picks), afterPicks);

	return {
		action,
		rejected,
		noop,
		autoAdded,
		autoRemoved,
		priceBefore: committed.total,
		priceAfter: preview.total,
	};
}

/**
 * View-model dell'azione in sospeso mostrata nella finestra di conferma: azione,
 * conseguenze calcolate, domande da risolvere, scelte correnti, possibilità di
 * confermare e accessori resi non selezionabili.
 */
export interface PendingVM {
	action: Action;
	consequences: Consequences;
	orGroups: OrGroup[];
	choices: OrChoice[];
	canConfirm: boolean;
	/** Opzioni rese non selezionabili dall'azione, escluse quelle già in autoRemoved. */
	excluded: OptionId[];
}
